import {
  DomainAddressType,
  DomainBorrowerEmploymentStatus,
  DomainEthnicityType,
  DomainExpenseType,
  DomainIncomeType,
  DomainLandSituationType,
  DomainRaceType,
} from "@porchpass/api-client";
import type {
  DomainCitizenshipType,
  DomainMaritalStatusType,
  DomainManufacturerType,
  DomainBiologicalSexType,
  DtoBorrowerUpdateReq,
  DtoCreateAddressReq,
  DtoCreateEmploymentReq,
  DtoCreateAssetReq,
  DtoCreateIncomeReq,
  DtoCreateExpenseReq,
  DtoUpdateHomeReq,
  DtoUpdateEmploymentReq,
  DtoUpdateEmploymentAddress,
  DtoUpdateEmploymentIncome,
  DtoUpdateHomeAddress,
  DtoBorrowerConsentUpdateReq,
  DtoUpdateDemographicsReq,
  DtoBiologicalSex,
  DtoEthnicity,
  DtoRace,
  DtoCreateLandAlreadyOwnedReq,
  DtoCreateLandCurrentlyFinancedReq,
  DtoCreateLandToBePurchasedReq,
  DtoCreateLandToBeLeasedReq,
  DtoCreateLandFamilyReq,
  DtoCreateLandReservationReq,
  // GET response types
  DtoBorrower,
  DtoAddress,
  DtoEmploymentDetail,
  DtoAsset,
  DtoIncome,
  DtoExpense,
  DtoHome,
  DtoGetLandResp,
  DtoGetDemographicsResp,
  DomainAssetType,
  DtoApplicationSummaryUpdateReqBody,
} from "@porchpass/api-client";
import { DomainFrequencyType } from "@porchpass/api-client";
import type {
  BorrowerData,
  Address,
  Employment,
  FinancialAccount,
  AdditionalIncome,
  Expense,
  HomeData,
  PricingData,
  LandData,
} from "../types/applicationTypes";
import type { DtoGetLandRespWithPorchpass } from "src/utils/porchpassBlockReasons";

/**
 * Strips empty/default values from an API request body so only filled-in
 * fields are sent. Used for **create** requests where omitted fields
 * should use server defaults.
 *
 * Removes: undefined, null, and empty strings ("").
 * Keeps: numbers (including 0), booleans, arrays, objects.
 */
export function stripEmptyFields<T extends object>(obj: T): Partial<T> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined || value === null || value === "") continue;
    result[key] = value;
  }
  return result as Partial<T>;
}

/**
 * Prepares an API request body for **update** (PUT/PATCH) requests.
 * Unlike stripEmptyFields, this converts empty strings to null so the
 * backend clears previously-saved values instead of silently keeping them.
 *
 * Removes: undefined.
 * Converts: empty strings ("") → null.
 * Keeps: null, numbers (including 0), booleans, arrays, objects.
 */
export function stripEmptyFieldsForUpdate<T extends object>(
  obj: T,
): Partial<T> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined) continue;
    result[key] = value === "" ? null : value;
  }
  return result as Partial<T>;
}

export function mapAddressToApi(address: Address): DtoCreateAddressReq {
  return {
    address_type:
      address.type === DomainAddressType.AddressCurrent ||
      address.type === DomainAddressType.AddressPrevious ||
      address.type === DomainAddressType.AddressMailing
        ? address.type
        : DomainAddressType.AddressCurrent,
    city: address.city,
    county: address.county,
    housing_type: address.housingType ?? undefined,
    housing_type_other: address.housingTypeOther ?? undefined,
    is_mailing_address: address.isMailingAddress,
    monthly_payment: address.monthlyPayment,
    resided_from:
      address.residedFromYear && address.residedFromMonth
        ? `${address.residedFromYear}-${address.residedFromMonth}-01T00:00:00Z`
        : undefined,
    resided_to:
      address.residedToYear && address.residedToMonth
        ? `${address.residedToYear}-${address.residedToMonth}-01T00:00:00Z`
        : undefined,
    state: address.state,
    street1: address.street,
    street2: address.unit,
    zip_code: address.zipCode,
  };
}

export function mapEmploymentToApi(
  employment: Employment,
): DtoCreateEmploymentReq {
  return {
    employer_name: employment.employerName,
    position_title: employment.jobTitle,
    start_date:
      employment.startYear && employment.startMonth
        ? `${employment.startYear}-${employment.startMonth}-01T00:00:00Z`
        : undefined,
    end_date:
      employment.endYear && employment.endMonth
        ? `${employment.endYear}-${employment.endMonth}-01T00:00:00Z`
        : undefined,
    is_current: employment.isCurrent,
    is_self_employed: employment.isSelfEmployed ?? false,
    address: employment.employerAddress
      ? mapAddressToApi(employment.employerAddress)
      : undefined,
    incomes:
      employment.incomes.length > 0
        ? employment.incomes.map((inc) => ({
            amount: inc.amount,
            frequency: inc.frequency,
            income_type: inc.incomeType,
            weekly_hours:
              inc.incomeType === DomainIncomeType.IncomeOvertime
                ? inc.overtimeHours
                : inc.weeklyHours,
          }))
        : undefined,
  };
}

export function mapAssetToApi(account: FinancialAccount): DtoCreateAssetReq {
  return {
    asset_type: account.accountType,
    institution_name: account.institutionName,
    balance_amount: account.balance,
  };
}

export function mapIncomeToApi(income: AdditionalIncome): DtoCreateIncomeReq {
  const isChildSupportOrSSI =
    income.type === DomainIncomeType.IncomeChildSupport ||
    income.type === DomainIncomeType.IncomeSSIForDependents;

  // Child support / SSI for dependents: no dates (DLA pattern). Send "" to clear any stale backend dates.
  // Other types: build ISO date strings from month/year. When ongoing, send "" to clear end_date.
  const startDate = isChildSupportOrSSI
    ? ""
    : income.startYear && income.startMonth
      ? `${income.startYear}-${income.startMonth}-01T00:00:00Z`
      : undefined;
  // When ongoing, send a zero date to clear end_date on the backend.
  // The backend update handler ignores null/undefined (treats as "no change"),
  // so we must send a non-nil value. The zero date acts as a sentinel.
  const endDate = isChildSupportOrSSI
    ? ""
    : income.isOngoing
      ? "0001-01-01T00:00:00Z"
      : income.endYear && income.endMonth
        ? `${income.endYear}-${income.endMonth}-01T00:00:00Z`
        : undefined;

  return {
    income_type: income.type,
    income_type_other: income.typeOther,
    amount: income.monthlyAmount,
    frequency: DomainFrequencyType.FrequencyMonthly,
    start_date: startDate,
    end_date: endDate,
    children_ages: isChildSupportOrSSI
      ? income.childrenAges?.length
        ? income.childrenAges
        : undefined
      : undefined,
  };
}

export function mapExpenseToApi(expense: Expense): DtoCreateExpenseReq {
  const isChildSupport = expense.type === DomainExpenseType.ExpenseChildSupport;

  // Only CHILD_SUPPORT has start date. Send "" for others to clear stale values.
  const startDate = isChildSupport
    ? expense.startYear && expense.startMonth
      ? `${expense.startYear}-${expense.startMonth}-01T00:00:00Z`
      : undefined
    : "";

  // When ongoing, send a zero date to clear end_date on the backend.
  // The backend update handler ignores null/undefined (treats as "no change"),
  // so we must send a non-nil value. The zero date acts as a sentinel.
  const endDate = expense.isOngoing
    ? "0001-01-01T00:00:00Z"
    : expense.endYear && expense.endMonth
      ? `${expense.endYear}-${expense.endMonth}-01T00:00:00Z`
      : undefined;

  return {
    expense_type: expense.type,
    expense_type_other: expense.typeOther,
    amount: expense.monthlyAmount,
    start_date: startDate,
    end_date: endDate,
    children_ages: isChildSupport
      ? expense.childrenAges?.length
        ? expense.childrenAges
        : undefined
      : undefined,
  };
}

export function normalizeDependentsAgesYearsForApi(
  ages: number[] | undefined,
): number[] {
  if (!ages?.length) return [];
  return ages;
}

export function mapBorrowerToApi(borrower: BorrowerData): DtoBorrowerUpdateReq {
  return {
    first_name: borrower.firstName,
    last_name: borrower.lastName,
    middle_name: borrower.middleName,
    suffix: borrower.suffix,
    email: borrower.email,
    phone_number: borrower.phone,
    citizenship_type: borrower.citizenship as DomainCitizenshipType | undefined,
    marital_status_type: borrower.maritalStatus as
      DomainMaritalStatusType | undefined,
    dependents_ages_years: normalizeDependentsAgesYearsForApi(
      borrower.dependentsAgesYears,
    ),
    employment_status: borrower.employmentStatus || undefined,
    nearest_relative_name: borrower.nearestRelativeName,
    nearest_relative_relationship: borrower.nearestRelativeRelationship,
    nearest_relative_phone_number: borrower.nearestRelativePhone || undefined,
    bankruptcy_date:
      borrower.hasBankruptcy === true && borrower.bankruptcyDate
        ? `${borrower.bankruptcyDate}T00:00:00Z`
        : borrower.hasBankruptcy === false
          ? "0001-01-01T00:00:00Z"
          : undefined,
  };
}

export function mapConsentToApi(
  borrower: BorrowerData,
  dirtyFields?: Set<string>,
): DtoBorrowerConsentUpdateReq {
  const full: DtoBorrowerConsentUpdateReq = {
    date_of_birth: borrower.dateOfBirth
      ? `${borrower.dateOfBirth}T00:00:00Z`
      : "0001-01-01T00:00:00Z",
    ssn: borrower.ssn
      ? `${borrower.ssn.slice(0, 3)}-${borrower.ssn.slice(3, 5)}-${borrower.ssn.slice(5)}`
      : "",
    is_acknowledged: true,
  };

  if (!dirtyFields) return full;

  const FIELD_MAP: Record<string, keyof DtoBorrowerConsentUpdateReq> = {
    dateOfBirth: "date_of_birth",
    ssn: "ssn",
  };
  const partial: Record<string, unknown> = { is_acknowledged: true };
  for (const field of dirtyFields) {
    const key = field.replace(/^(primaryBorrower|coBorrower)\./, "");
    const apiKey = FIELD_MAP[key];
    if (apiKey) partial[apiKey] = full[apiKey];
  }
  return partial;
}

export function mapHomeToApi(
  home: HomeData,
  pricing?: PricingData,
  propertyAddress?: Address,
): DtoUpdateHomeReq {
  const address: DtoUpdateHomeAddress | undefined = propertyAddress
    ? {
        street1: propertyAddress.street,
        street2: propertyAddress.unit,
        city: propertyAddress.city,
        county: propertyAddress.county,
        state: propertyAddress.state,
        zip_code: propertyAddress.zipCode,
      }
    : undefined;

  return {
    section_type:
      home.homeType === "OTHER" && !home.homeTypeOther
        ? undefined
        : home.homeType || undefined,
    section_type_other: home.homeTypeOther ?? undefined,
    usage_type: home.primaryUse || undefined,
    usage_type_other: home.usageTypeOther ?? undefined,
    model_name: home.modelName ?? undefined,
    model_year: home.modelYear ?? undefined,
    manufacturer_type: (home.manufacturer || undefined) as
      DomainManufacturerType | undefined,
    manufacturer_type_other: home.manufacturerOther || undefined,
    price: pricing?.homePrice ?? undefined,
    down_payment_amount: pricing?.downPaymentAmount ?? undefined,
    down_payment_source_type:
      pricing?.downPaymentSource !== undefined &&
      pricing.downPaymentSource !== ""
        ? pricing.downPaymentSource
        : undefined,
    down_payment_source_other: pricing?.downPaymentSourceOther ?? undefined,
    upgrades_cost: pricing?.upgradesCost ?? undefined,
    site_improvement_cost: pricing?.siteImprovementCost ?? undefined,
    address,
  };
}

export function mapPricingToSummaryApi(
  pricing: PricingData,
): DtoApplicationSummaryUpdateReqBody {
  return {
    // Backend validates home_price with gt=0; omit when unset/zero so
    // down-payment-only autosaves do not fail validation.
    home_price:
      pricing.homePrice != null && pricing.homePrice > 0
        ? pricing.homePrice
        : undefined,
    home_upgrades_cost: pricing.upgradesCost ?? undefined,
    site_improvement_amount: pricing.siteImprovementCost ?? undefined,
    down_payment_amount: pricing.downPaymentAmount ?? undefined,
    home_insurance_yearly_payment: pricing.yearlyPremium ?? undefined,
    land_purchase_or_payoff_amount:
      pricing.landPurchaseOrPayoffAmount ?? undefined,
  };
}

// ============================================================================
// New Frontend → API Mappers
// ============================================================================

// Ethnicity/race types that require an "other" text value
const ETHNICITY_TYPES_REQUIRING_OTHER: Set<string> = new Set([
  DomainEthnicityType.EthnicityOtherHispanicOrLatino,
]);
const RACE_TYPES_REQUIRING_OTHER: Set<string> = new Set([
  DomainRaceType.RaceAmericanIndianAlaskaNative,
  DomainRaceType.RaceOtherAsian,
  DomainRaceType.RaceOtherPacificIslander,
]);

export function mapDemographicsToApi(
  borrower: BorrowerData,
): DtoUpdateDemographicsReq {
  const ethnicities: DtoEthnicity[] | undefined = borrower.ethnicities
    ?.filter((e) => !ETHNICITY_TYPES_REQUIRING_OTHER.has(e.type) || e.other)
    .map((e) => ({
      ethnicity_type: e.type as DtoEthnicity["ethnicity_type"],
      ethnicity_other: e.other,
    }));

  const races: DtoRace[] | undefined = borrower.races
    ?.filter((r) => !RACE_TYPES_REQUIRING_OTHER.has(r.type) || r.other)
    .map((r) => ({
      race_type: r.type as DtoRace["race_type"],
      race_other: r.other,
    }));

  const biological_sexes: DtoBiologicalSex[] | undefined =
    borrower.biologicalSex
      ? [
          {
            biological_sex_type:
              borrower.biologicalSex as DomainBiologicalSexType,
          },
        ]
      : undefined;

  return {
    ethnicities,
    races,
    biological_sexes,
  };
}

export function mapEmploymentToUpdateApi(
  employment: Employment,
): DtoUpdateEmploymentReq {
  const addr = employment.employerAddress;
  const address: DtoUpdateEmploymentAddress | undefined = addr
    ? {
        street1: addr.street,
        street2: addr.unit,
        city: addr.city,
        county: addr.county,
        state: addr.state,
        zip_code: addr.zipCode,
      }
    : undefined;

  const incomes: DtoUpdateEmploymentIncome[] | undefined =
    employment.incomes.length > 0
      ? employment.incomes.map((inc) => ({
          amount: inc.amount,
          frequency: inc.frequency,
          income_type: inc.incomeType,
          weekly_hours:
            inc.incomeType === DomainIncomeType.IncomeOvertime
              ? inc.overtimeHours
              : inc.weeklyHours,
        }))
      : undefined;

  return {
    employer_name: employment.employerName,
    position_title: employment.jobTitle,
    start_date:
      employment.startYear && employment.startMonth
        ? `${employment.startYear}-${employment.startMonth}-01T00:00:00Z`
        : undefined,
    end_date:
      employment.endYear && employment.endMonth
        ? `${employment.endYear}-${employment.endMonth}-01T00:00:00Z`
        : undefined,
    is_current: employment.isCurrent,
    is_self_employed: employment.isSelfEmployed ?? false,
    address,
    incomes,
  };
}

export function mapLandToApi(
  land: LandData,
):
  | DtoCreateLandAlreadyOwnedReq
  | DtoCreateLandCurrentlyFinancedReq
  | DtoCreateLandToBePurchasedReq
  | DtoCreateLandToBeLeasedReq
  | DtoCreateLandFamilyReq
  | DtoCreateLandReservationReq {
  const purchaseDate =
    land.purchaseDateYear && land.purchaseDateMonth
      ? `${land.purchaseDateYear}-${land.purchaseDateMonth}-01T00:00:00Z`
      : undefined;

  switch (land.landSituation) {
    case DomainLandSituationType.LandSituationTypeAlreadyOwned:
      return {
        land_value: land.estimatedLandValue,
        use_as_down_payment: land.useAsDownPayment,
        down_payment_amount: land.downPaymentAmount,
        acreage: land.acreage,
        purchase_date: purchaseDate,
        has_frontage_public_road: land.hasFrontagePublicRoad,
        has_paved_road: land.hasPavedRoad,
        hoa_fee_amount: land.hoaFeeAmount,
      } satisfies DtoCreateLandAlreadyOwnedReq;

    case DomainLandSituationType.LandSituationTypeCurrentlyFinanced:
      return {
        monthly_payment: land.monthlyLandPayment,
        use_as_down_payment: land.useAsDownPayment,
        is_refinance: land.isRefinance,
        purchase_price_payoff: land.existingMortgageBalance,
        land_value: land.estimatedLandValue,
        down_payment_amount: land.downPaymentAmount,
        acreage: land.acreage,
        purchase_date: purchaseDate,
        land_owner: land.landlordName,
        has_frontage_public_road: land.hasFrontagePublicRoad,
        has_paved_road: land.hasPavedRoad,
        hoa_fee_amount: land.hoaFeeAmount,
      } satisfies DtoCreateLandCurrentlyFinancedReq;

    case DomainLandSituationType.LandSituationTypePurchasing:
      return {
        purchase_price_payoff: land.existingMortgageBalance,
        acreage: land.acreage,
        land_owner: land.landlordName,
        has_frontage_public_road: land.hasFrontagePublicRoad,
        has_paved_road: land.hasPavedRoad,
        hoa_fee_amount: land.hoaFeeAmount,
      } satisfies DtoCreateLandToBePurchasedReq;

    case DomainLandSituationType.LandSituationTypeLeasing:
      return {
        land_owner: land.landlordName,
        lease_type: land.leaseType,
        monthly_payment: land.monthlyLeasePayment,
        price_increase_explanation: land.priceIncreaseExplanation,
        hoa_fee_amount: land.hoaFeeAmount,
      } satisfies DtoCreateLandToBeLeasedReq;

    case DomainLandSituationType.LandSituationTypeFamily:
      return {} satisfies DtoCreateLandFamilyReq;

    case DomainLandSituationType.LandSituationReservation:
      return {} satisfies DtoCreateLandReservationReq;

    default:
      return {} satisfies DtoCreateLandFamilyReq;
  }
}

// ============================================================================
// API → Frontend Reverse Mappers (for loading existing applications)
// ============================================================================

export function mapApiBorrowerToFrontend(dto: DtoBorrower): BorrowerData {
  return {
    firstName: dto.first_name ?? "",
    middleName: dto.middle_name ?? "",
    lastName: dto.last_name ?? "",
    suffix: dto.suffix ?? "",
    email: dto.email ?? "",
    phone: dto.phone_number ?? "",
    citizenship: (dto.citizenship_type as BorrowerData["citizenship"]) ?? "",
    dateOfBirth: dto.date_of_birth?.slice(0, 10),
    ssn: dto.ssn?.replace(/\D/g, ""),
    hasBankruptcy:
      dto.bankruptcy_date && !dto.bankruptcy_date.startsWith("0001-01-01")
        ? true
        : dto.bankruptcy_date?.startsWith("0001-01-01")
          ? false
          : undefined,
    bankruptcyDate:
      dto.bankruptcy_date && !dto.bankruptcy_date.startsWith("0001-01-01")
        ? dto.bankruptcy_date.slice(0, 10)
        : undefined,
    maritalStatus:
      (dto.marital_status_type as BorrowerData["maritalStatus"]) ?? "",
    dependentsAgesYears: normalizeDependentsAgesYearsForApi(
      dto.dependents_ages_years,
    ),
    nearestRelativeName: dto.nearest_relative_name ?? "",
    nearestRelativeRelationship: dto.nearest_relative_relationship ?? "",
    nearestRelativePhone: dto.nearest_relative_phone_number ?? "",
    employmentStatus: dto.employment_status,
    hasSufficientAddressHistory: false,
    hasSufficientEmploymentHistory: false,
    addresses: [],
    employmentHistory: [],
    financialAccounts: [],
    additionalIncome: [],
    expenses: [],
  };
}

export function mapApiAddressToFrontend(dto: DtoAddress): Address {
  const residedFrom = dto.resided_from ? dto.resided_from.split("-") : [];
  const residedTo = dto.resided_to ? dto.resided_to.split("-") : [];

  return {
    serverId: dto.id,
    street: dto.street1 ?? "",
    unit: dto.street2,
    city: dto.city ?? "",
    state: dto.state ?? "",
    zipCode: dto.zip_code ?? "",
    county: dto.county,
    type: dto.address_type ?? DomainAddressType.AddressCurrent,
    isMailingAddress: dto.is_mailing_address ?? false,
    residedFromYear: residedFrom[0],
    residedFromMonth: residedFrom[1],
    residedToYear: residedTo[0],
    residedToMonth: residedTo[1],
    housingType: dto.housing_type,
    housingTypeOther: dto.housing_type_other,
    monthlyPayment: dto.monthly_payment,
  };
}

export function mapApiEmploymentToFrontend(
  dto: DtoEmploymentDetail,
): Employment {
  const emp = dto.employment;
  const addr = dto.address;
  const startParts = emp?.start_date ? emp.start_date.split("-") : [];
  const endParts = emp?.end_date ? emp.end_date.split("-") : [];

  return {
    serverId: emp?.id,
    employerName: emp?.employer_name ?? "",
    jobTitle: emp?.position_title ?? "",
    startYear: startParts[0],
    startMonth: startParts[1],
    endYear: endParts[0],
    endMonth: endParts[1],
    isCurrent: emp?.is_current ?? false,
    isSelfEmployed: emp?.is_self_employed,
    employmentType: DomainBorrowerEmploymentStatus.BorrowerEmploymentEmployed,
    incomes: (dto.incomes ?? []).map((inc) => {
      const incomeType =
        (inc.income_type as DomainIncomeType) ?? DomainIncomeType.IncomeBase;
      const isOvertime = incomeType === DomainIncomeType.IncomeOvertime;
      return {
        serverId: inc.id,
        incomeType,
        amount: inc.amount ?? 0,
        frequency:
          (inc.frequency as DomainFrequencyType) ??
          DomainFrequencyType.FrequencyMonthly,
        weeklyHours: isOvertime ? undefined : inc.weekly_hours,
        overtimeHours: isOvertime ? inc.weekly_hours : undefined,
      };
    }),
    employerAddress: addr
      ? {
          serverId: addr.id,
          street: addr.street1 ?? "",
          unit: addr.street2,
          city: addr.city ?? "",
          state: addr.state ?? "",
          zipCode: addr.zip_code ?? "",
          county: addr.county,
          type: DomainAddressType.AddressCurrent,
          isMailingAddress: false,
        }
      : undefined,
  };
}

export function mapApiAssetToFrontend(dto: DtoAsset): FinancialAccount {
  return {
    serverId: dto.id,
    accountType: dto.asset_type ?? ("" as DomainAssetType),
    institutionName: dto.institution_name ?? "",
    balance: dto.balance_amount ?? 0,
  };
}

/** Check if a date string is the zero sentinel used to clear end_date */
function isZeroDate(date: string | undefined): boolean {
  return !!date && date.startsWith("0001-01-01");
}

export function mapApiIncomeToFrontend(dto: DtoIncome): AdditionalIncome {
  const startParts = dto.start_date?.slice(0, 10).split("-");
  const endDateStr =
    dto.end_date && !isZeroDate(dto.end_date) ? dto.end_date : undefined;
  const endParts = endDateStr?.slice(0, 10).split("-");

  return {
    serverId: dto.id,
    type: dto.income_type ?? ("" as DomainIncomeType),
    typeOther: dto.income_type_other,
    monthlyAmount: dto.amount ?? 0,
    startMonth: startParts?.[1],
    startYear: startParts?.[0],
    endMonth: endParts?.[1],
    endYear: endParts?.[0],
    isOngoing: dto.start_date && !endDateStr ? true : undefined,
    childrenAges: dto.children_ages,
  };
}

export function mapApiExpenseToFrontend(dto: DtoExpense): Expense {
  const startParts = dto.start_date?.slice(0, 10).split("-");
  const endDateStr =
    dto.end_date && !isZeroDate(dto.end_date) ? dto.end_date : undefined;
  const endParts = endDateStr?.slice(0, 10).split("-");

  return {
    serverId: dto.id,
    type: dto.expense_type ?? ("" as DomainExpenseType),
    typeOther: dto.expense_type_other,
    monthlyAmount: dto.amount ?? 0,
    startMonth: startParts?.[1],
    startYear: startParts?.[0],
    endMonth: endParts?.[1],
    endYear: endParts?.[0],
    isOngoing: dto.start_date && !endDateStr ? true : undefined,
    childrenAges: dto.children_ages,
  };
}

export function mapApiDemographicsToFrontend(
  dto: DtoGetDemographicsResp,
): Pick<BorrowerData, "ethnicities" | "races" | "biologicalSex"> {
  return {
    ethnicities: dto.ethnicities?.map((e) => ({
      type: e.ethnicity_type ?? "",
      other: e.ethnicity_other,
    })),
    races: dto.races?.map((r) => ({
      type: r.race_type ?? "",
      other: r.race_other,
    })),
    biologicalSex: dto.biological_sexes?.[0]?.biological_sex_type ?? "",
  };
}

export function mapApiHomeToFrontend(dto: DtoHome): {
  home: HomeData;
  pricing: Partial<PricingData>;
} {
  return {
    home: {
      manufacturer: dto.manufacturer_type ?? "",
      manufacturerOther: dto.manufacturer_type_other,
      modelName: dto.model_name ?? "",
      modelYear: dto.model_year ?? undefined,
      homeType: (dto.section_type as HomeData["homeType"]) ?? "",
      homeTypeOther: dto.section_type_other,
      primaryUse: (dto.usage_type as HomeData["primaryUse"]) ?? "",
      usageTypeOther: dto.usage_type_other,
      isNew: true,
      occupancyType: "owner-occupied",
    },
    pricing: {
      homePrice: dto.price ?? 0,
      downPaymentAmount: dto.down_payment_amount ?? 0,
      downPaymentSource: dto.down_payment_source_type ?? "",
      downPaymentSourceOther: dto.down_payment_source_other ?? "",
      upgradesCost: dto.upgrades_cost ?? 0,
      siteImprovementCost: dto.site_improvement_cost ?? 0,
    },
  };
}

export function mapApiHomeAddressToFrontend(dto: DtoHome): Address | undefined {
  if (!dto.address) return undefined;
  const addr = dto.address;
  return {
    street: addr.street1 ?? "",
    unit: addr.street2,
    city: addr.city ?? "",
    state: addr.state ?? "",
    zipCode: addr.zip_code ?? "",
    county: addr.county,
    type: DomainAddressType.AddressCurrent,
    isMailingAddress: false,
  };
}

export function mapApiLandToFrontend(dto: DtoGetLandResp): LandData {
  const porchpassDto = dto as DtoGetLandRespWithPorchpass;
  const purchaseParts = dto.purchase_date ? dto.purchase_date.split("-") : [];
  const situation =
    (dto.land_situation_type as LandData["landSituation"]) ?? "";

  return {
    landSituation: situation,
    estimatedLandValue: dto.land_value,
    acreage: dto.acreage,
    hasFrontagePublicRoad: dto.has_frontage_public_road,
    hasPavedRoad: dto.has_paved_road,
    hoaFeeAmount: dto.hoa_fee_amount,
    purchaseDateYear: purchaseParts[0],
    purchaseDateMonth: purchaseParts[1],
    useAsDownPayment: dto.use_as_down_payment,
    downPaymentAmount: dto.down_payment_amount,
    monthlyLandPayment:
      situation !== DomainLandSituationType.LandSituationTypeLeasing
        ? dto.monthly_payment
        : undefined,
    existingMortgageBalance: dto.purchase_price_payoff,
    isRefinance: dto.is_refinance,
    landlordName: dto.land_owner,
    leaseType: dto.lease_type,
    monthlyLeasePayment:
      situation === DomainLandSituationType.LandSituationTypeLeasing
        ? dto.monthly_payment
        : undefined,
    hasPriceIncrease:
      dto.price_increase_explanation != null &&
      dto.price_increase_explanation !== "",
    priceIncreaseExplanation: dto.price_increase_explanation,
    supportedByPorchpass: porchpassDto.supported_by_porchpass,
    blockReasons: porchpassDto.block_reasons,
  };
}
